import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

/**
 * i18n bootstrap.
 *
 * Boot rules (stakeholder decision 2026-04-23):
 *  - AR is the default locale.
 *  - LanguageDetector may override to 'en' ONLY if browser strongly prefers EN
 *    (i.e. navigator.language starts with 'en'); otherwise fall back to 'ar'.
 *  - User's explicit choice (localStorage 'ga-erp-lang') always wins.
 *
 * localStorage use is allowed for UI preferences only (TC-008).
 */

const LS_KEY = 'ga-erp-lang';
const NAMESPACES = [
  'common',
  'dashboard',
  'finance',
  'engineering',
  'operations',
  'contracts',
  'hr',
  'marketing',
  'legal',
  'settings',
  'workflows',
];

function pickInitialLanguage(): 'ar' | 'en' {
  if (typeof window === 'undefined') return 'ar';
  const stored = window.localStorage.getItem(LS_KEY);
  if (stored === 'ar' || stored === 'en') return stored;
  const nav = window.navigator.language?.toLowerCase() ?? 'ar';
  return nav.startsWith('en') ? 'en' : 'ar';
}

const initialLang = pickInitialLanguage();

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: initialLang,
    fallbackLng: 'ar',
    supportedLngs: ['ar', 'en'],
    ns: NAMESPACES,
    defaultNS: 'common',
    backend: {
      loadPath: `${import.meta.env.BASE_URL}locales/{{lng}}/{{ns}}.json`,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LS_KEY,
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  });

i18n.on('languageChanged', (lng) => {
  try {
    window.localStorage.setItem(LS_KEY, lng);
  } catch {
    // localStorage can be blocked — fail soft
  }
});

export default i18n;
